import type {
    PROSJEKT_TYPE,
    ProsjektSteg,
    REGISTRERING_STATUS,
    Aktivitet,
} from '@klp/formgenerator/interfaces';
import type { VerifiedEnvelope } from './services/typesVerified';

export enum ProjectRegions {
    Oslo = 'Oslo',
    Trondheim = 'Trondheim',
    Sverige = 'Sverige',
    Danmark = 'Danmark',
    Europa = 'Europa',
}

export type GodkjenningProsessBase = {
    // Base fields
    prosjektId: string;
    prosessInstansId: string;
    navn: string | null;
    startDato: string;
    lastUpdated: string | null;
    fullfortDato: string | null;

    prosjektskjemaType: ProsjektskjemaType;
    type: PROSJEKT_TYPE;
    typeNavn: string;
    registreringStatus: REGISTRERING_STATUS;
    registreringStatusNavn: string;
    steg: PROSJEKT_SUBTYPE;
    stegNavn: string;

    prosessDefinisjonKey: string;
    prosjektStatus: string | null;
    tillattNesteSteg: Array<ProsjektSteg>;

    // Fields from form
    prosjekteier: string | null;
    prosjektleder: string | null;
    d365Prosjektnummer: string | null;
    byggnummer: string | null;
};

export type GodkjenningProsessSimple = GodkjenningProsessBase & {
    ansvarlig: string | null;
    kandidatGruppe: string | null;
    kandidatGruppeEier: string | null;
};

export type GodkjenningProsessResponse = GodkjenningProsessBase & {
    prosjekt: Prosjekt | null;
    startetAvBruker?: string;
    verifiedEnvelopeId?: string;
    signeringKonvolutt?: VerifiedEnvelope;

    // Assigned users
    prosjekteierFulltnavn?: string;
    prosjektlederFulltnavn?: string;
    prosjektkontroller?: string;
    prosjektkontrollerFulltnavn?: string;
    forvaltningsdirektor?: string;
    forvaltningsdirektorFulltnavn?: string;
    utviklingsdirektor?: string;
    utviklingsdirektorFulltnavn?: string;
    okonomiFulltnavn?: string;
    okonomisjef?: string;
    okonomisjefFulltnavn?: string;
    regiondirektorFulltnavn?: string;
    administrerendedirektorFulltnavn?: string;
    prosjektdirektorFulltnavn?: string;
    teknisksjefFulltnavn?: string;
    eiendomssjefFulltnavn?: string;

    // Fields from form
    avvistKommentar?: string;
    region: ProjectRegions | null;

    // Additional Camunda fields
    prosessDefinisjonId?: string;

    historiskeProsessSteg: Array<ProsjektProsess>;
    aktiviteter?: Array<Aktivitet>;
    currentAktivitet?: Aktivitet;
};

type Prosjekt = {
    id?: string;
    navn?: string;
    status?: string;
    type?: string;
    beskrivelse?: string;
    opprettetAv?: string;
    endretAv?: string;
};

type ProsjektProsess = {
    type?: PROSJEKT_TYPE;
    typeNavn?: string;
    steg?: PROSJEKT_SUBTYPE;
    stegNavn?: string;
    registreringStatus?: REGISTRERING_STATUS;
    registreringStatusNavn?: string;
    prosjekteier?: string;
    prosjektleder?: string;
    prosjektkontroller?: string;
    forvaltningsdirektor?: string;
    utviklingsdirektor?: string;
    administrerendesdirektor?: string;
    okonomisjef?: string;
    prosessInstansId?: string;
    prosessDefinisjonId?: string;
    prosessDefinisjonKey?: string;
    verifiedEnvelopeId?: string;
    prosjekt?: Prosjekt;
    prosjektId?: string;
    navn?: string;
    startDato?: string;
    fullfortDato?: string;
    startetAvBruker?: string;
    aktiviteter?: Array<Aktivitet>;
    currentAktivitet?: Aktivitet;
    prosjektskjemaType: ProsjektskjemaType;
};

export enum PROSJEKT_TYPE_TRANSLATION {
    LTT = 'project.types.LTT',
    TEK = 'project.types.TEK',
    KOM = 'project.types.KOM',
}

export type ProsjektskjemaType = {
    id: number;
    rekkefolgenummer: number;
    typeNavn?: string;
};

export type PROSJEKT_SUBTYPE =
    | PROSJEKT_SUBTYPE_LTT
    | PROSJEKT_SUBTYPE_KOM
    | PROSJEKT_SUBTYPE_TEK
    | PROSJEKT_SUBTYPE_TILTAK;

export enum PROSJEKT_SUBTYPE_LTT {
    TILBUD = 5,
    LONNSOMHETSBEREGNING = 4,
    FORPROSJEKT = 0,
    GJENNOMFORING = 1,
    ENDRING = 2,
    AVSLUTNING = 3,
}

export enum PROSJEKT_SUBTYPE_KOM {
    SKISSE,
    FORPROSJEKT,
    GJENNOMFORING,
    ENDRING,
    AVSLUTNING,
}

export enum PROSJEKT_SUBTYPE_TEK {
    FORPROSJEKT,
    GJENNOMFORING,
    ENDRING,
    AVSLUTNING,
}

export enum PROSJEKT_SUBTYPE_TILTAK {
    GJENNOMFORING = 1,
    AVSLUTNING = 3,
}

export type ProjectTypeStatus = {
    typeNavn?: string;
    prosjektNavn?: string;
    prosjektStatus?: string;
    prosjektskjemaType?: string;
};

export const PROSJEKT_SKJEMA_TYPE_IDS = {
    HOVEDPROSJEKT: 0,
    UNDERPROSJEKT: 1,
} as const;
